import type { PulseChart } from '@shared/chart';
import type { EngineState } from '@shared/engine/pulse/state';
import { comboMultiplier } from '@shared/scoring';
import { comboHudTier } from './pulseRenderColour';

/**
 * Stateless HUD pass: score, combo + multiplier tier, judgement tally,
 * progress %, song title + progress bar, and the centred HP bar.
 *
 * HUD is intentionally one big draw pass (not split into score/combo/etc
 * helpers) because everything shares the same save/restore stack and the
 * `ctx.font` / `ctx.fillStyle` ordering matters frame-to-frame. Splitting
 * further would mean more save/restore overhead for no real maintenance
 * win.
 */

export function drawHud(
  ctx: CanvasRenderingContext2D,
  state: EngineState,
  beat: number,
  chart: PulseChart,
  w: number,
): void {
  ctx.save();
  ctx.font = 'bold 22px "Rajdhani", "Orbitron", system-ui, sans-serif';
  ctx.fillStyle = '#fff';
  ctx.shadowColor = '#16e0ff';
  ctx.shadowBlur = 10;
  ctx.textAlign = 'left';
  ctx.fillText(state.score.toLocaleString(), 16, 32);

  const mult = comboMultiplier(state.combo);
  const tier = comboHudTier(state.combo);
  ctx.font = 'bold 14px "Rajdhani", system-ui, sans-serif';
  ctx.fillStyle = tier.colour;
  ctx.shadowColor = tier.glowColour;
  ctx.shadowBlur = tier.glow;
  const multText = `x${mult}`;
  const comboLine =
    state.combo > 0 ? `${multText}  \u00B7  ${state.combo} combo` : multText;
  ctx.fillText(comboLine, 16, 54);

  ctx.font = '11px monospace';
  ctx.fillStyle = '#8e96c7';
  ctx.shadowBlur = 0;
  ctx.fillText(`P${state.perfects}  G${state.goods}  M${state.misses}`, 16, 72);

  ctx.textAlign = 'right';
  ctx.font = 'bold 14px monospace';
  ctx.fillStyle = '#fff';
  ctx.shadowColor = '#16e0ff';
  ctx.shadowBlur = 8;
  const progress = Math.max(0, Math.min(1, beat / chart.totalBeats));
  ctx.fillText(`${Math.round(progress * 100)}%`, w - 16, 28);
  ctx.font = '11px monospace';
  ctx.fillStyle = '#8e96c7';
  ctx.shadowBlur = 0;
  ctx.fillText(chart.title.toUpperCase(), w - 16, 46);

  const barW = 160;
  const barH = 4;
  const barX = w - 16 - barW;
  const barY = 54;
  ctx.fillStyle = 'rgba(255,255,255,0.15)';
  ctx.fillRect(barX, barY, barW, barH);
  ctx.fillStyle = chart.palette.pad;
  ctx.fillRect(barX, barY, barW * progress, barH);

  drawHpBar(ctx, state, w);
  ctx.restore();
}

/**
 * Centred 0-100 HP gauge above the song progress bar. Colour shifts from
 * green to amber to red as the player drains; provides immediate "you're
 * about to die" peripheral signal without needing a glance at the number.
 */
function drawHpBar(
  ctx: CanvasRenderingContext2D,
  state: EngineState,
  w: number,
): void {
  const max = state.maxHp;
  if (max <= 0) {
    return;
  }
  const ratio = Math.max(0, Math.min(1, state.hp / max));
  const barW = 220;
  const barH = 14;
  const x = (w - barW) / 2;
  const y = 22;

  ctx.save();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.fillRect(x, y, barW, barH);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, barW - 1, barH - 1);

  const colour = ratio > 0.5 ? '#3bff9d' : ratio > 0.25 ? '#ffae3b' : '#ff3b5c';
  ctx.fillStyle = colour;
  ctx.shadowColor = colour;
  ctx.shadowBlur = 12;
  ctx.fillRect(x + 1, y + 1, (barW - 2) * ratio, barH - 2);
  ctx.shadowBlur = 0;

  ctx.font = 'bold 11px monospace';
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.fillText(`${Math.round(state.hp)} / ${max}`, w / 2, y + barH + 12);
  ctx.restore();
}
